import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Refresh as RefreshIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';
import HubSpotRecordsTable from './HubSpotRecordsTable';

const HubSpotIntegration = ({ initialTab = 0 }) => {
  const { user, loading: authLoading } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addonRequired, setAddonRequired] = useState(false);
  const [addonInfo, setAddonInfo] = useState(null);
  const [activatingAddon, setActivatingAddon] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  
  // Connection form state
  const [accessToken, setAccessToken] = useState('');
  
  // Normalize initialTab to ensure it's a number (0 or 1)
  const normalizedInitialTab = typeof initialTab === 'number' ? initialTab : (typeof initialTab === 'string' ? parseInt(initialTab, 10) : 0);
  const safeInitialTab = (normalizedInitialTab === 0 || normalizedInitialTab === 1) ? normalizedInitialTab : 0;
  
  // Tab state
  const [activeTab, setActiveTab] = useState(safeInitialTab);
  
  // Update activeTab when initialTab prop changes
  useEffect(() => {
    const normalized = typeof initialTab === 'number' ? initialTab : (typeof initialTab === 'string' ? parseInt(initialTab, 10) : 0);
    const safe = (normalized === 0 || normalized === 1) ? normalized : 0;
    setActiveTab(safe);
  }, [initialTab]);
  
  // Check connection status on mount
  useEffect(() => {
    if (user && !authLoading) {
      checkConnectionStatus();
    }
  }, [user, authLoading]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
    setSuccess(null);
  };
  
  const checkConnectionStatus = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await api.hubspotStatus();
      
      if (result.success) {
        // Check if add-on is required
        if (result.addon_required) {
          setAddonRequired(true);
          setAddonInfo({
            code: result.addon_code || 'hubspot',
            message: result.message || 'HubSpot add-on required'
          });
          setConnectionStatus({ connected: false, connection: null });
        } else {
          setAddonRequired(false);
          setAddonInfo(null);
          setConnectionStatus(result);
          if (result.connected && result.connection) {
            // Don't pre-fill the access token for security
            setAccessToken('');
          }
        }
      } else {
        setConnectionStatus({ connected: false, connection: null });
        setAddonRequired(false);
        setError(result.error || 'Failed to check connection status');
      }
    } catch (err) {
      logger.error('Error checking HubSpot status:', err);
      setConnectionStatus({ connected: false, connection: null });
      setAddonRequired(false);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.response?.status === 403 && err.response?.data?.addon_required) {
        // Add-on required error
        setAddonRequired(true);
        setAddonInfo({
          code: err.response.data.addon_code || 'hubspot',
          message: err.response.data.error || 'HubSpot add-on required'
        });
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to check connection status');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubscribeToAddon = async () => {
    if (!addonInfo) return;
    
    try {
      setActivatingAddon(true);
      setError(null);
      
      // Get available add-ons to find the HubSpot add-on
      const addonsResponse = await api.getAddOns();
      if (addonsResponse.success && addonsResponse.data?.addons) {
        const hubspotAddon = addonsResponse.data.addons.find(a => a.code === 'hubspot');
        if (hubspotAddon) {
          const result = await api.activateAddOn(hubspotAddon.id);
          if (result.success) {
            setSuccess('HubSpot add-on activated successfully!');
            setAddonRequired(false);
            setAddonInfo(null);
            setShowAddonDialog(false);
            // Refresh connection status
            await checkConnectionStatus();
          } else {
            setError(result.message || 'Failed to activate add-on');
          }
        } else {
          setError('HubSpot add-on is not available yet. Please contact support or check back later.');
          logger.error('HubSpot add-on not found in available add-ons:', addonsResponse.data.addons);
        }
      } else {
        const errorMsg = addonsResponse.message || 'Failed to load add-ons. Please ensure you have an active subscription.';
        setError(errorMsg);
        logger.error('Failed to load add-ons:', addonsResponse);
      }
    } catch (err) {
      logger.error('Error subscribing to add-on:', err);
      setError(err.response?.data?.message || 'Failed to subscribe to add-on');
    } finally {
      setActivatingAddon(false);
    }
  };
  
  const handleConnect = async () => {
    if (!user) {
      setError('Please log in to use HubSpot integration');
      return;
    }
    
    // Check if add-on is required
    if (addonRequired) {
      setError('Please subscribe to the HubSpot add-on to connect. Click "Subscribe to Add-On" above.');
      return;
    }
    
    // Verify session is still valid before making API call
    try {
      const authCheck = await api.get('/check_login');
      if (!authCheck.data.logged_in) {
        setError('Your session has expired. Please log in again.');
        return;
      }
    } catch (err) {
      logger.error('Error verifying session:', err);
      setError('Your session has expired. Please log in again.');
      return;
    }
    
    if (!accessToken || !accessToken.trim()) {
      setError('Personal access token is required');
      return;
    }
    
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);
      
      const result = await api.hubspotConnect(accessToken.trim());
      
      if (result.success) {
        setSuccess('Successfully connected to HubSpot!');
        // Clear access token for security
        setAccessToken('');
        await checkConnectionStatus();
        setActiveTab(0); // Stay on connection tab
      } else {
        setError(result.error || 'Failed to connect to HubSpot');
      }
    } catch (err) {
      logger.error('Error connecting to HubSpot:', err);
      // Check if it's an add-on required error
      if (err.response?.status === 403 && err.response?.data?.addon_required) {
        setAddonRequired(true);
        setAddonInfo({
          code: err.response.data.addon_code || 'hubspot',
          message: err.response.data.error || 'HubSpot add-on required'
        });
        setError('Please subscribe to the HubSpot add-on to connect.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to connect to HubSpot');
      }
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async () => {
    if (!user) {
      setError('Please log in to disconnect HubSpot');
      return;
    }
    
    if (!window.confirm('Are you sure you want to disconnect from HubSpot? This will remove your connection.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await api.hubspotDisconnect();
      
      if (result.success) {
        setSuccess('Successfully disconnected from HubSpot');
        setConnectionStatus({ connected: false, connection: null });
        setAccessToken('');
      } else {
        setError(result.error || 'Failed to disconnect from HubSpot');
      }
    } catch (err) {
      logger.error('Error disconnecting from HubSpot:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to disconnect from HubSpot');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Show loading state
  if (authLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show message if user is not authenticated
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          HubSpot Integration
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please log in to use HubSpot integration.
        </Alert>
      </Box>
    );
  }
  
  // Show add-on required message
  if (addonRequired && !showAddonDialog) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          HubSpot Integration
        </Typography>
        
        <Card sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CloudOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                HubSpot Add-On Required
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To use HubSpot integration, you need to subscribe to the HubSpot add-on.
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                The HubSpot add-on includes:
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Connect to HubSpot CRM" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Create contacts in HubSpot" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Update existing contacts" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Check for duplicate contacts" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Sync contact data" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Bulk contact operations" />
                </ListItem>
              </List>
            </Alert>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setShowAddonDialog(true)}
              >
                View Details
              </Button>
              <Button
                variant="contained"
                onClick={handleSubscribeToAddon}
                disabled={activatingAddon}
                startIcon={activatingAddon ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {activatingAddon ? 'Activating...' : 'Subscribe to Add-On'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        HubSpot Integration
      </Typography>
      
      <Paper sx={{ mt: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Connection" />
          <Tab 
            label="Records" 
            icon={connectionStatus?.connected ? <CloudDoneIcon sx={{ color: '#FF7A59' }} /> : <CloudOffIcon />}
            iconPosition="end"
          />
        </Tabs>
        
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Connection Status */}
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Checking HubSpot connection...
                </Typography>
              </Box>
            )}
            
            {!loading && connectionStatus && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {connectionStatus.connected ? (
                        <>
                          <CloudDoneIcon sx={{ color: '#FF7A59' }} />
                          <Typography variant="h6">Connected to HubSpot</Typography>
                          <Chip 
                            label="Active" 
                            color="success" 
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </>
                      ) : (
                        <>
                          <CloudOffIcon />
                          <Typography variant="h6">Not Connected</Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        startIcon={<RefreshIcon />}
                        onClick={checkConnectionStatus}
                        size="small"
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                      {connectionStatus.connected && (
                        <Button
                          startIcon={<LinkOffIcon />}
                          onClick={handleDisconnect}
                          size="small"
                          color="error"
                          disabled={loading}
                        >
                          Disconnect
                        </Button>
                      )}
                    </Box>
                  </Box>
                  
                  {connectionStatus.connection && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Connection created: {new Date(connectionStatus.connection.created_at).toLocaleString()}
                      </Typography>
                      {connectionStatus.connection.last_synced_at && (
                        <Typography variant="body2" color="text.secondary">
                          Last synced: {new Date(connectionStatus.connection.last_synced_at).toLocaleString()}
                        </Typography>
                      )}
                      {connectionStatus.connection.last_error && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Last error: {connectionStatus.connection.last_error}
                        </Alert>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Success/Error Messages */}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {/* Connection Form */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {connectionStatus?.connection ? 'Update HubSpot Connection' : 'Connect to HubSpot'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {connectionStatus?.connection 
                    ? 'Update your HubSpot personal access token to reconnect. Your token is encrypted and stored securely.'
                    : 'Enter your HubSpot personal access token to connect. Your token is encrypted and stored securely.'}
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" component="div">
                    <strong>How to get your personal access token:</strong>
                    <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      <li>Log in to your HubSpot account</li>
                      <li>Go to <strong>Settings → Integrations → Private Apps</strong></li>
                      <li>Create a new private app or select an existing one</li>
                      <li>Go to the <strong>"Scopes"</strong> tab and ensure these scopes are enabled:
                        <ul style={{ marginTop: 4, marginBottom: 0 }}>
                          <li>crm.objects.contacts.read</li>
                          <li>crm.objects.contacts.write</li>
                          <li>crm.objects.companies.read</li>
                          <li>crm.objects.companies.write</li>
                        </ul>
                      </li>
                      <li>Go to the <strong>"Personal Access Token"</strong> tab (NOT the Overview tab)</li>
                      <li>Click <strong>"Create token"</strong> or view an existing token</li>
                      <li>Copy the token that starts with <strong>"pat-na1-"</strong> or <strong>"pat-eu1-"</strong></li>
                      <li style={{ marginTop: 8, color: '#d32f2f', fontWeight: 'bold' }}>
                        ⚠️ Important: The token MUST start with "pat-". If you see a token starting with "CiRu..." or other characters, that's NOT the correct token.
                      </li>
                    </ol>
                  </Typography>
                </Alert>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
                  <TextField
                    label="Personal Access Token"
                    type="password"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    fullWidth
                    required
                    disabled={connecting}
                    helperText={connectionStatus?.connection ? "Enter new token to update connection" : "Your HubSpot personal access token"}
                    placeholder="pat-na1-..."
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    disabled={connecting || !accessToken.trim()}
                    sx={{
                      backgroundColor: '#FF7A59',
                      '&:hover': {
                        backgroundColor: '#e66a4a'
                      },
                      maxWidth: 200
                    }}
                  >
                    {connecting ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                        Connecting...
                      </>
                    ) : (
                      connectionStatus?.connection ? 'Update Connection' : 'Connect to HubSpot'
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
        
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {!connectionStatus?.connected ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Not Connected
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please connect to HubSpot in the Connection tab to view and manage contacts.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(0)}
                  sx={{
                    backgroundColor: '#FF7A59',
                    '&:hover': {
                      backgroundColor: '#e66a4a'
                    }
                  }}
                >
                  Go to Connection Tab
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">HubSpot Contacts</Typography>
                </Box>
                <HubSpotRecordsTable />
              </>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Add-On Details Dialog */}
      <Dialog
        open={showAddonDialog}
        onClose={() => setShowAddonDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Subscribe to HubSpot Add-On
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The HubSpot add-on enables full integration with your HubSpot CRM, including:
          </Typography>
          <List dense sx={{ mt: 1 }}>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Connect to HubSpot CRM" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Create contacts in HubSpot" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Update existing contacts" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Check for duplicate contacts" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Sync contact data" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Bulk contact operations" />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              The add-on is billed monthly and can be cancelled anytime. You'll continue to have access until the end of your billing period.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddonDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubscribeToAddon}
            disabled={activatingAddon}
            startIcon={activatingAddon ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {activatingAddon ? 'Activating...' : 'Subscribe Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HubSpotIntegration;

